/**
 * Road vehicles of the expansion (10 §2): the missile transporter-erector of mission 17 and the
 * general's staff car of mission 29. Both are parked nose to the SOUTH at yaw 0, so the camera sees
 * windscreens, headlamps and - on the launcher - the warm nose of the missile between the two cabs.
 */

import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { sided, snowPatch, tubeZ, wheel } from './parts';

/** Where the missile lies on its cradle: shared with the wreck, which breaks it in two. */
export const LAUNCHER_MISSILE = { y: 2.42, r: 0.48, tail: -3.9, shoulder: 2.9, tip: 4.0 };

/**
 * Headlamps, a shade proud of the blocks in the bodies below. Both models are also driven as MOVING
 * vehicles (ROUND4 §1: `launcherTruck`, `staffCar`), and `world/Machines` draws these self-lit after
 * dark - a lorry coming down the road at night is the one time a machine announces itself early.
 */
export const mobileLauncherLamps: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (const sx of [-1, 1]) mb.box(0.26, 0.22, 0.05, p.color('fire.3'), place(sx * 0.97, 1.14, 4.1));
  return mb;
};

export const staffCarLamps: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (const sx of [-1, 1]) mb.box(0.26, 0.24, 0.05, p.color('fire.3'), place(sx * 0.55, 0.71, 2.16));
  return mb;
};

/**
 * Eight-wheeled transporter-erector-launcher with one large missile lying along its back, nose
 * forward between the split cabs - the silhouette that says what it is. Army drab (the canvas
 * ramp); the missile is pale steel with the same warm band and nose as the SAMs of mission 7.
 */
export const mobileLauncher: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const drab = sided(p, 'canvas', p.color('canvas.hi'));
  for (const sx of [-1, 1]) {
    for (const z of [-3.0, -1.45, 0.95, 2.5]) wheel(mb, p, [sx * 1.25, 0.52, z], 0.6, 0.4, 6);
  }
  mb.box(2.2, 0.5, 8.0, p.color('canvas.lo'), place(0, 0.55, 0));
  mb.box(2.9, 0.6, 6.2, drab, place(0, 1.05, -0.95));
  // Split cabs either side of the nose, windscreens and headlamps to the south.
  for (const sx of [-1, 1]) {
    mb.box(0.95, 1.1, 1.7, drab, place(sx * 0.97, 1.05, 3.2));
    mb.box(0.75, 0.42, 0.06, p.color('ink.1'), place(sx * 0.97, 1.6, 4.06));
    mb.box(0.24, 0.2, 0.08, p.color('fire.3'), place(sx * 0.97, 1.15, 4.06));
    snowPatch(mb, p, sx * 0.97, 2.15, 3.15, 0.6, 1.1);
    snowPatch(mb, p, sx * 1.1, 1.65, -1.2 + sx * 0.8, 0.5, 1.9);
  }
  // Engine deck behind the cabs, and the enemy's band across the tail where the camera sees it.
  mb.box(1.5, 0.06, 0.9, p.color('ink.1'), place(0, 1.65, 1.55));
  mb.box(2.92, 0.06, 0.5, p.color('enemyAccent.0'), place(0, 1.65, -3.7));
  // Erector beam, two saddles, and the missile itself.
  const m = LAUNCHER_MISSILE;
  mb.box(0.7, 0.22, 6.6, p.color('metal.lo'), place(0, 1.65, -0.7));
  for (const z of [-2.6, 1.2]) mb.box(1.2, 0.3, 0.4, p.color('metal.lo'), place(0, 1.8, z));
  tubeZ(mb, 0, m.y, m.tail, m.shoulder, m.r, m.r, 8, p.color('metal.hi'), p.color('ink.1'));
  tubeZ(mb, 0, m.y, m.shoulder - 0.35, m.shoulder, m.r + 0.03, m.r + 0.03, 8, p.color('fire.2'));
  tubeZ(mb, 0, m.y, m.shoulder, m.tip, m.r, 0, 8, p.color('fire.0'));
  mb.box(1.5, 0.06, 0.7, p.color('metal.mid'), place(0, m.y - 0.03, m.tail + 0.4));
  mb.box(0.06, 1.5, 0.7, p.color('metal.mid'), place(0, m.y - 0.75, m.tail + 0.4));
  snowPatch(mb, p, 0, m.y + m.r - 0.02, -1.6, 0.4, 2.2);
  snowPatch(mb, p, 0, m.y + m.r - 0.02, 1.3, 0.4, 1.3);
  return mb;
};

/**
 * Staff car: long bonnet, dark glass under a hard roof, wings over the wheels and a pennant on the
 * offside wing. Painted in the enemy's uniform ramp, which is chosen to stand off every biome's ground.
 */
export const staffCar: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const lo = p.color('uniformEnemy.0');
  const paint: BoxColors = { all: p.color('uniformEnemy.1'), top: p.color('uniformEnemy.2'), left: lo, right: lo };
  for (const sx of [-1, 1]) {
    for (const z of [-1.35, 1.35]) {
      wheel(mb, p, [sx * 0.78, 0.32, z], 0.37, 0.22, 6);
      mb.box(0.3, 0.1, 0.95, lo, place(sx * 0.82, 0.68, z));
    }
  }
  mb.box(1.66, 0.55, 4.2, paint, place(0, 0.3, 0));
  mb.box(1.3, 0.32, 1.55, paint, place(0, 0.85, 1.25));
  mb.box(1.4, 0.26, 0.75, paint, place(0, 0.85, -1.7));
  // Cabin: a band of dark glass with the roof sitting on it.
  mb.box(1.5, 0.58, 1.85, p.color('ink.1'), place(0, 0.85, -0.45));
  mb.box(1.58, 0.13, 1.95, { all: lo, top: p.color('uniformEnemy.1') }, place(0, 1.43, -0.45));
  snowPatch(mb, p, 0.05, 1.56, -0.5, 1.0, 1.2);
  snowPatch(mb, p, -0.1, 1.17, 1.3, 0.8, 0.9);
  // Grille, headlamps, bumpers.
  mb.box(0.7, 0.36, 0.06, p.color('ink.0'), place(0, 0.62, 2.1));
  for (const sx of [-1, 1]) mb.box(0.24, 0.22, 0.1, p.color('fire.3'), place(sx * 0.55, 0.72, 2.1));
  for (const z of [-2.14, 2.14]) mb.box(1.7, 0.12, 0.1, p.color('metal.lo'), place(0, 0.34, z));
  // The pennant, flying square to the camera: whose car this is.
  mb.box(0.05, 0.62, 0.05, p.color('metal.lo'), place(0.74, 0.74, 1.85));
  mb.box(0.36, 0.22, 0.04, p.color('enemyAccent.0'), place(0.56, 1.14, 1.85));
  return mb;
};
